/**
 * AuthBox Component
 * Login form for store-front authentication
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { POButton, POButtonType, POLinkButton } from '@prizeout/prizeout-ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Alert } from '../../components/alert/alert';
import { FormInput } from '../../components/form-input/form-input';
import { Loader } from '../../components/loader';
import { Tooltip, TooltipDirection } from '../../components/tooltip';
import { useGoToRoute, useStorefrontAuth } from '../../hooks';
import { routes } from '../../utils/routes';
import { validationRules } from '../../utils/validation';

import './auth-box.scss';

interface AuthFormInputs {
    accessToken: string;
    pin: string;
}

export const AuthBox: React.FC = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { goToRoute } = useGoToRoute();

    const { isLoading, error, parseCredentialsFromUrl, checkExistingSession, performLogin } = useStorefrontAuth();

    const [showForm, setShowForm] = useState(false);
    const [hasAttemptedAutoLogin, setHasAttemptedAutoLogin] = useState(false);
    const urlAccessCodeRef = useRef<string | null>(null);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors, touchedFields },
        clearErrors,
    } = useForm<AuthFormInputs>({
        mode: 'onBlur',
    });

    // Check for URL credentials or existing session on mount
    useEffect(() => {
        const initAuth = async () => {
            try {
                const credentials = parseCredentialsFromUrl(location, searchParams);

                if (credentials) {
                    urlAccessCodeRef.current = credentials.accessToken;

                    setValue('accessToken', credentials.accessToken);
                    setValue('pin', credentials.pin);

                    const success = await performLogin(credentials.accessToken, credentials.pin);
                    if (success) {
                        goToRoute(routes.OFFERS, undefined, { replace: true });
                        return;
                    }

                    // Auto-login failed — fall through to show the form with the API error
                }

                // No URL credentials (or they failed) — check for an existing session
                const hasSession = await checkExistingSession();
                if (hasSession) {
                    goToRoute(routes.OFFERS, undefined, { replace: true });
                    return;
                }
            } catch {
                // Unexpected error — surface the form so the user is never stuck on the loader
            }

            setHasAttemptedAutoLogin(true);
            setShowForm(true);
        };

        if (!hasAttemptedAutoLogin) {
            initAuth();
        }
    }, [
        location,
        searchParams,
        parseCredentialsFromUrl,
        checkExistingSession,
        performLogin,
        setValue,
        goToRoute,
        hasAttemptedAutoLogin,
    ]);

    const onSubmit: SubmitHandler<AuthFormInputs> = useCallback(
        async (data) => {
            clearErrors();
            setShowForm(false);

            const trimmed = {
                accessToken: data.accessToken.trim(),
                pin: data.pin.trim(),
            };

            const success = await performLogin(trimmed.accessToken, trimmed.pin);
            if (success) {
                // Small delay to ensure smooth transition to offers page
                await new Promise((resolve) => setTimeout(resolve, 300));
                goToRoute(routes.OFFERS);
            } else {
                // Show form again if login failed
                setShowForm(true);
            }
        },
        [clearErrors, performLogin, goToRoute],
    );

    const handleForgotPassword = useCallback(() => {
        const accessCode = urlAccessCodeRef.current || getValues('accessToken') || '';
        goToRoute(routes.FORGOT_PASSWORD, undefined, { state: { accessCode } });
    }, [getValues, goToRoute]);

    const hasFormErrors = Object.keys(errors).length > 0;
    const showErrors = hasFormErrors || !!error;

    // Show fullscreen loading screen while checking auth or performing auto-login
    if (!showForm) {
        return <Loader fullPage pulse size="small" />;
    }

    return (
        <div className="auth-box">
            <div className="auth-box__content">
                <div className="auth-box__header">
                    <h1 className="auth-box__title">Welcome to Prizeout</h1>
                    <p className="auth-box__subtitle">
                        Enter your access code and passcode to access your funds.
                        <Tooltip
                            content="Your access code & passcode can be found in your email from Prizeout"
                            direction={TooltipDirection.DOWN}
                        />
                    </p>
                </div>

                {showErrors && (
                    <div className="auth-box__alerts">
                        {error && <Alert type="error" message={error} />}
                        {errors.accessToken && touchedFields.accessToken && (
                            <Alert type="error" message={errors.accessToken.message || 'Invalid access code'} />
                        )}
                        {errors.pin && touchedFields.pin && (
                            <Alert type="error" message={errors.pin.message || 'Invalid passcode'} />
                        )}
                    </div>
                )}

                <form
                    id="auth-box-form"
                    className="auth-box__form"
                    onSubmit={handleSubmit(onSubmit)}
                    autoComplete="off"
                >
                    <FormInput
                        id="accessToken"
                        label="Access Code"
                        type="text"
                        placeholder="Access Code"
                        error={!!errors.accessToken && touchedFields.accessToken}
                        {...register('accessToken', validationRules.accessToken)}
                    />

                    <FormInput
                        id="pin"
                        label="Passcode"
                        type="password"
                        placeholder="Passcode"
                        error={!!errors.pin && touchedFields.pin}
                        {...register('pin', validationRules.pin)}
                    />

                    <div>
                        <POLinkButton label="Forgot your passcode?" onClick={handleForgotPassword} />
                    </div>
                </form>

                <div className="auth-box__actions">
                    <POButton
                        label="Access Funds"
                        isLoading={isLoading}
                        formId="auth-box-form"
                        type={POButtonType.SUBMIT}
                    />
                </div>
            </div>
        </div>
    );
};

export default AuthBox;
